/**
 * src/lib/api.ts
 *
 * Typed API call wrappers — aligned with actual backend endpoints.
 *
 */

import { apiClient } from './api-client';
import type {
  AnalyticsOverview,
  LoginRequest,
  TokenResponse,
  UserInToken,
  DashboardStats,
  DashboardFilters,
  PaginationParams,
  SocShiftStatus,
  SocUser,
  SocUserCreate,
  ClientUser,
  ClientUserCreate,
  ClientUserUpdate,
  ClientUserListResponse,
  Company,
  CompanyListResponse,
  Incident,
  SOSAlert,
  GuestLog,
  Site,
  PaginatedResponse,
  MpesaPayment,
  MpesaPaymentListResponse,
  MpesaMatchRequest,
  MpesaActivateRequest,
  MpesaRejectRequest,
  ManualSubscriptionRequest,
  BillingDashboard,
  ClientKPIs,
  ClientGuardList,
  ClientShiftList,
  ClientIncidentList,
  ClientSOSList,
  ClientSubscription,
  GuardLivePosition,
  ShiftMonitorRow,
 ComplianceStats,
 SiteBreakdown,
 ReportDateParams,
 AttendanceReportData,
 IncidentsReportData,
 GuardsReportData,
 HRReportData,
 ComplianceTrendData,
 MissedShiftsData,
 ShiftTemplate,
 ScansReportData,
 OccurrenceBookReportData,
 GuestLogsReportData,
 LeaveListResponse, 
 LeaveDecisionPayload, 
 AdvanceListResponse, 
 AdvanceDecisionPayload,
 CustomerOverview,
 CustomerSiteRow,
 CustomerStaffList,
 CustomerShiftList,
 CustomerAdminRow,
 CustomerAdminCreate,
 CustomerAdminUpdate,
 CustomerUserAdminRow,
 CustomerUserAdminCreate,
 CustomerUserAdminListResponse,
  
} from './types';

// Fallback dynamic types to fix apiClient query structural checks
type QueryParams = Record<string, any>;

// ── Auth ───────────────────────────────────────────────────────────────────

export const authApi = {
  login: async (credentials: LoginRequest): Promise<UserInToken> => {
    const data = await apiClient.post<TokenResponse>('/auth/web/login', credentials);
    return data.user_in_token;
  },
  logout: async (): Promise<void> => {
    try {
      await apiClient.post('/auth/logout', {});
    } catch { /* best-effort */ }
  },
  me: () => apiClient.get<UserInToken>('/auth/me'),
  changePassword: (payload: {
    current_password: string;
    new_password: string;
    confirm_password: string;
  }) => apiClient.post('/auth/change-password', payload),
};

// ── SOC Dashboard ─────────────────────────────────────────────────────────

export const socDashboardApi = {
  getStats: (
    filters?: DashboardFilters,
    sosPagination?: PaginationParams,
    incidentPagination?: PaginationParams,
    companyId?: string,
  ) =>
    apiClient.get<DashboardStats>('/soc-dashboard/stats', {
      company_id: companyId,
      status:        filters?.status,
      severity:      filters?.severity,
      guard_id:      filters?.guard_id,
      incident_type: filters?.incident_type,
      skip:          sosPagination?.skip  ?? 0,
      limit:         sosPagination?.limit ?? 20,
    } as QueryParams),
  getMyShift: () => apiClient.get<SocShiftStatus>('/soc-dashboard/my-shift'),
  clockIn:    () => apiClient.post<{ message: string; clock_in_time: string; log_id: string }>('/soc-dashboard/clock-in'),
  clockOut:   () => apiClient.post<{ message: string; clock_in_time: string; clock_out_time: string; hours_worked: number }>('/soc-dashboard/clock-out'),
};

// ── Analytics ─────────────────────────────────────────────────────────────

export const analyticsApi = {
  getOverview: (params?: {
    range?: '1m' | '3m' | '6m' | '1y';
    company_id?: string;
  }) => apiClient.get<AnalyticsOverview>('/analytics/overview', params as QueryParams),
};

// ── Companies ─────────────────────────────────────────────────────────────

export const companiesApi = {
  list: (params?: PaginationParams & { search?: string; is_active?: boolean }) =>
    apiClient.get<CompanyListResponse>('/companies', params as QueryParams),
  get: (id: string) => apiClient.get<Company>(`/companies/${id}`),
  dropdown: () =>
    apiClient.get<{ id: string; name: string }[]>('/companies/dropdown'),
};

// ── Incidents ─────────────────────────────────────────────────────────────

export const incidentsApi = {
  list: (params?: PaginationParams & {
    company_id?: string;
    filter_status?: string;
    incident_type?: string;
    guard_id?: string;
  }) => apiClient.get<PaginatedResponse<Incident>>('/incidents', params as QueryParams),

  allOpen: (params?: { page?: number; page_size?: number }) =>
    apiClient.get<PaginatedResponse<Incident>>('/incidents/all-open', params as QueryParams),

  get:     (id: string) => apiClient.get<Incident>(`/incidents/${id}`),
  review:  (id: string) => apiClient.post<Incident>(`/incidents/${id}/review`),
  resolve: (id: string, note?: string) =>
    apiClient.post<Incident>(`/incidents/${id}/resolve`, note ? { note } : {}),
  close:   (id: string) => apiClient.post<Incident>(`/incidents/${id}/close`),
};

// ── SOS Alerts ────────────────────────────────────────────────────────────

export const sosApi = {
  listAllActive: (params?: PaginationParams) =>
    apiClient.get<SOSAlert[]>('/sos/all-active', params as QueryParams),
  list: (params?: PaginationParams & { company_id?: string; status?: string; severity?: string }) =>
    apiClient.get<SOSAlert[]>('/sos', params as QueryParams),
  get:     (id: string) => apiClient.get<SOSAlert>(`/sos/${id}`),
  respond: (id: string) => apiClient.post<SOSAlert>(`/sos/${id}/respond`, {}),
  resolve: (id: string) => apiClient.post<SOSAlert>(`/sos/${id}/resolve`, {}),
};

// ── Guest Logs ────────────────────────────────────────────────────────────

export const guestsApi = {
  list: (params?: PaginationParams & {
    company_id?: string;
    site_id?: string;
    active_only?: boolean;
    date_from?: string;
    date_to?: string;
    status?: string;
    date?: string;
  }) =>
    apiClient.get<PaginatedResponse<GuestLog>>('/guests', params as QueryParams),
  get: (id: string) => apiClient.get<GuestLog>(`/guests/${id}`),
  checkout: (id: string) => apiClient.post<GuestLog>(`/guests/${id}/checkout`),
  update: (id: string, payload: Partial<Pick<GuestLog,
    'guest_name' | 'id_number' | 'host_name' | 'purpose' | 'vehicle_plate'
  >>) => apiClient.patch<GuestLog>(`/guests/${id}`, payload),

  // Raw fetch, not apiClient — this endpoint returns image/png bytes
  // directly, and apiClient.get always calls res.json() on success,
  // which would throw on a binary body. Same pattern as the PDF
  // exporters in reportsApi.
  fetchGuestSignature: async (logId: string, which: 'entry' | 'exit'): Promise<Blob> => {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_API_URL}/guests/${logId}/signature/${which}`,
      { method: 'GET', credentials: 'include' }
    );
    if (!response.ok) {
      throw new Error(`No ${which} signature found`);
    }
    return response.blob();
  },
};

// ── Sites ─────────────────────────────────────────────────────────────────

export const sitesApi = {
  list: (params?: PaginationParams & { company_id?: string }) =>
    apiClient.get<Site[]>('/sites', params as QueryParams),
  get: (id: string) => apiClient.get<Site>(`/sites/${id}`),
};

// ── SOC Users (self) ──────────────────────────────────────────────────────

export const socUsersApi = {
  me: () => apiClient.get<SocUser>('/soc-users/me'),
};

// ── SOC Admin — user management ───────────────────────────────────────────

export const socAdminApi = {
  listSocUsers: async (params?: PaginationParams & { role?: string; is_active?: boolean }) => {
    const users = await apiClient.get<SocUser[]>('/admin/soc-users', params as QueryParams);
    return { users, total: users.length };
  },
  getSocUser:    (id: string)  => apiClient.get<SocUser>(`/admin/soc-users/${id}`),
  createSocUser: (payload: SocUserCreate) => apiClient.post<SocUser>('/admin/soc-users', payload),
  updateSocRole: (id: string, role: 'soc' | 'soc_admin') =>
    apiClient.patch<SocUser>(`/admin/soc-users/${id}/role`, { role }),
  deactivateSocUser: (id: string) => apiClient.patch<SocUser>(`/admin/soc-users/${id}/deactivate`),
  reactivateSocUser: (id: string) => apiClient.patch<SocUser>(`/admin/soc-users/${id}/reactivate`),

  // Billing
  getBillingDashboard: () => apiClient.get<BillingDashboard>('/admin/billing'),
  getBillingCompany:   (id: string) => apiClient.get(`/admin/billing/companies/${id}`),
  suspendCompany:      (id: string, reason?: string) =>
    apiClient.post(`/admin/billing/companies/${id}/suspend`, { reason }),
  activateCompany:     (id: string) =>
    apiClient.post(`/admin/billing/companies/${id}/activate`),
  createManualSub:     (payload: ManualSubscriptionRequest) =>
    apiClient.post('/admin/billing/subscriptions/manual', payload),
};

// ── SOC Admin — Client user management ───────────────────────────────────

export const clientMgmtApi = {
  list: (params?: PaginationParams & { company_id?: string; is_active?: boolean }) =>
    apiClient.get<ClientUserListResponse>('/soc/clients', params as QueryParams),
  get:    (id: string) => apiClient.get<ClientUser>(`/soc/clients/${id}`),
  create: (payload: ClientUserCreate) => apiClient.post<ClientUser>('/soc/clients', payload),
  update: (id: string, payload: ClientUserUpdate) =>
    apiClient.patch<ClientUser>(`/soc/clients/${id}`, payload),
  deactivate:    (id: string) => apiClient.delete<{ message: string }>(`/soc/clients/${id}`),
  resetPassword: (id: string) =>
    apiClient.post<{ message: string; temp_password: string }>(`/soc/clients/${id}/reset-password`),
};

// Location for live map and pings
export const locationsApi = {
  getAllLive: () => apiClient.get<GuardLivePosition[]>('/soc/guards/live'),
};

// ── Mpesa payments (soc_admin only) ──────────────────────────────────────

export const mpesaApi = {
  listPayments: (params?: PaginationParams & {
    status?: string;
    company_id?: string;
  }) => apiClient.get<MpesaPaymentListResponse>('/payment/payments', params as QueryParams), // Changed /mpesa to /payment

  getPayment: (id: string) => apiClient.get<MpesaPayment>(`/payment/payments/${id}`), // Changed /mpesa to /payment
  
  matchPayment: (id: string, payload: MpesaMatchRequest) =>
    apiClient.post<MpesaPayment>(`/payment/payments/${id}/match`, payload), // Changed /mpesa to /payment
    
  activateFromPayment: (id: string, payload: MpesaActivateRequest) =>
    apiClient.post<MpesaPayment>(`/payment/payments/${id}/activate`, payload), // Changed /mpesa to /payment
    
  rejectPayment: (id: string, payload: MpesaRejectRequest) =>
    apiClient.post<MpesaPayment>(`/payment/payments/${id}/reject`, payload), // Changed /mpesa to /payment
};

// ── Client Dashboard (client role — read-only, scoped to own company) ─────

// ── CLIENT DASHBOARD ─────────────────────────────────────────────

export const clientDashboardApi = {
  getOverview: () =>
    apiClient.get<ClientKPIs>('/client/overview'),

  getGuards: (params?: {
    limit?: number;
    skip?: number;
  }) => {
    const q = new URLSearchParams();

    if (params?.limit) q.set('limit', String(params.limit));
    if (params?.skip) q.set('skip', String(params.skip));

    return apiClient.get<ClientGuardList>(
      `/client/guards${q.toString() ? `?${q}` : ''}`
    );
  },

  getSupervisors: (params?: {
    limit?: number;
    skip?: number;
  }) => {
    const q = new URLSearchParams();

    if (params?.limit) q.set('limit', String(params.limit));
    if (params?.skip) q.set('skip', String(params.skip));

    return apiClient.get(
      `/client/supervisors${q.toString() ? `?${q}` : ''}`
    );
  },



  getIncidents: (params?: { 
    limit?: number; 
    skip?: number; 
    status?: string 
  }) =>
    apiClient.get<ClientIncidentList>('/client/incidents', params as QueryParams),

  getSOS: (params?: {
    limit?: number;
    skip?: number;
  }) => {
    const q = new URLSearchParams();

    if (params?.limit) q.set('limit', String(params.limit));
    if (params?.skip) q.set('skip', String(params.skip));

    return apiClient.get<ClientSOSList>(
      `/client/sos${q.toString() ? `?${q}` : ''}`
    );
  },

  getSubscription: () =>
    apiClient.get<ClientSubscription>('/client/subscription'),

  getGuardLocations: () =>
    apiClient.get('/client/guard-locations'),
  
  getShifts: (params?: {
    limit?: number;
    skip?: number;
    status?: string;
    date_from?: string;
    date_to?: string;
  }) =>
    apiClient.get<ClientShiftList>('/client/shifts', params as QueryParams),

};


export const shiftsApi = {
  monitor: (params?: {
    company_id?: string;
    filter_status?: string;
    role?: string;
    site_id?: string;
    date_from?: string;
    date_to?: string;
    skip?: number;
    limit?: number;
  }) => apiClient.get<{
    total: number;
    skip: number;
    limit: number;
    items: ShiftMonitorRow[];
  }>('/shifts/monitor', params as QueryParams),
};

export const shiftTemplatesApi = {
  list: (companyId: string, includeInactive = true) =>
    apiClient.get<ShiftTemplate[]>(
      `/shift-templates?company_id=${companyId}&include_inactive=${includeInactive}`
    ),
  toggleActive: (templateId: string, isActive: boolean) =>
    apiClient.patch(`/shift-templates/${templateId}`, { is_active: isActive }),
};

export const checkpointsApi = {
  stats: (params?: {
    company_id?: string;
    site_id?: string;
    date?: string;
  }) =>
    apiClient.get<ComplianceStats>(
      '/checkpoints/stats',
      params as QueryParams
    ),

    sites: (companyId: string) =>
      apiClient.get<{ id: string; name: string }[]>(
        '/sites',
        { company_id: companyId }
      ),
};

export const reportsApi = {
  getAttendance: (
    params: ReportDateParams
  ) =>
    apiClient.get<AttendanceReportData>(
      '/reports/attendance',
      params as QueryParams
    ),

  getIncidents: (
    params: ReportDateParams
  ) =>
    apiClient.get<IncidentsReportData>(
      '/reports/incidents',
      params as QueryParams
    ),

  getGuards: (
    params: ReportDateParams & {
      page?: number;
      page_size?: number;
    }
  ) =>
    apiClient.get<GuardsReportData>(
      '/reports/guards',
      params as QueryParams
    ),

  getHR: (
    params: ReportDateParams
  ) =>
    apiClient.get<HRReportData>(
      '/reports/hr',
      params as QueryParams
    ),

  getComplianceTrend: (
    params: {
      company_id?: string;
      days?: number;
    }
  ) =>
    apiClient.get<ComplianceTrendData>(
      '/reports/compliance-trend',
      params as QueryParams
    ),

  getMissedShifts: (
    params: ReportDateParams & {
      page?: number;
      page_size?: number;
    }
  ) =>
    apiClient.get<MissedShiftsData>(
      '/reports/attendance/missed',
      params as QueryParams
    ),


exportAttendancePdf: async (
  params: ReportDateParams
): Promise<void> => {
  const query = new URLSearchParams(
    Object.entries(params).reduce(
      (acc, [k, v]) => {
        if (v !== undefined && v !== null && v !== '') {
          acc[k] = String(v);
        }
        return acc;
      },
      {} as Record<string, string>
    )
  ).toString();

  const response = await fetch(
    `${process.env.NEXT_PUBLIC_API_URL}/reports/attendance/pdf?${query}`,
    {
      method: 'GET',
      credentials: 'include',
    }
  );

  if (!response.ok) {
    throw new Error('Failed to export attendance PDF');
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `shift-summary-${Date.now()}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
},

exportIncidentsPdf: async (
  params: ReportDateParams
): Promise<void> => {
  const query = new URLSearchParams(
    Object.entries(params).reduce(
      (acc, [k, v]) => {
        if (v !== undefined && v !== null && v !== '') {
          acc[k] = String(v);
        }
        return acc;
      },
      {} as Record<string, string>
    )
  ).toString();

  const response = await fetch(
    `${process.env.NEXT_PUBLIC_API_URL}/reports/incidents/pdf?${query}`,
    {
      method: 'GET',
      credentials: 'include',
    }
  );

  if (!response.ok) {
    throw new Error('Failed to export incidents PDF');
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `incident-report-${Date.now()}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
},

exportGuardsPdf: async (
  params: ReportDateParams
): Promise<void> => {
  const query = new URLSearchParams(
    Object.entries(params).reduce(
      (acc, [k, v]) => {
        if (v !== undefined && v !== null && v !== '') {
          acc[k] = String(v);
        }
        return acc;
      },
      {} as Record<string, string>
    )
  ).toString();

  const response = await fetch(
    `${process.env.NEXT_PUBLIC_API_URL}/reports/guards/pdf?${query}`,
    {
      method: 'GET',
      credentials: 'include',
    }
  );

  if (!response.ok) {
    throw new Error('Failed to export guard activity PDF');
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `guard-activity-${Date.now()}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
},

  getCheckpointScans: (
    params: ReportDateParams & {
      guard_id?: string;
      site_id?: string;
      checkpoint_id?: string;
      search?: string;
      page?: number;
      page_size?: number;
    }
  ) =>
    apiClient.get<ScansReportData>(
      '/reports/checkpoint-scans',
      params as QueryParams
    ),

  exportCheckpointScansPdf: async (
    params: ReportDateParams & {
      guard_id?: string;
      site_id?: string;
      checkpoint_id?: string;
      search?: string;
    }
  ): Promise<void> => {
    const query = new URLSearchParams(
      Object.entries(params).reduce(
        (acc, [k, v]) => {
          if (v !== undefined && v !== null && v !== '') {
            acc[k] = String(v);
          }
          return acc;
        },
        {} as Record<string, string>
      )
    ).toString();

    const response = await fetch(
      `${process.env.NEXT_PUBLIC_API_URL}/reports/checkpoint-scans/pdf?${query}`,
      { method: 'GET', credentials: 'include' }
    );

    if (!response.ok) {
      throw new Error('Failed to export checkpoint scans PDF');
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `checkpoint-scans-${Date.now()}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },
    getOccurrenceBook: (
    params: ReportDateParams & {
      site_id?: string;
      guard_id?: string;
      checkpoint_id?: string;
      search?: string;
      page?: number;
      page_size?: number;
    }
  ) =>
    apiClient.get<OccurrenceBookReportData>(
      '/reports/occurrence-book',
      params as QueryParams
    ),

  exportOccurrenceBookPdf: async (
    params: ReportDateParams & {
      site_id?: string;
      guard_id?: string;
      checkpoint_id?: string;
      search?: string;
    }
  ): Promise<void> => {
    const query = new URLSearchParams(
      Object.entries(params).reduce(
        (acc, [k, v]) => {
          if (v !== undefined && v !== null && v !== '') {
            acc[k] = String(v);
          }
          return acc;
        },
        {} as Record<string, string>
      )
    ).toString();

    const response = await fetch(
      `${process.env.NEXT_PUBLIC_API_URL}/reports/occurrence-book/pdf?${query}`,
      { method: 'GET', credentials: 'include' }
    );

    if (!response.ok) {
      throw new Error('Failed to export occurrence book PDF');
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `occurrence-book-${Date.now()}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },

  getGuestLogs: (
    params: ReportDateParams & {
      site_id?: string;
      guard_id?: string;
      active_only?: boolean;
      search?: string;
      page?: number;
      page_size?: number;
    }
  ) =>
    apiClient.get<GuestLogsReportData>(
      '/reports/guests',
      params as QueryParams
    ),

  exportGuestLogsPdf: async (
    params: ReportDateParams & {
      site_id?: string;
      guard_id?: string;
      active_only?: boolean;
      search?: string;
    }
  ): Promise<void> => {
    const query = new URLSearchParams(
      Object.entries(params).reduce(
        (acc, [k, v]) => {
          if (v !== undefined && v !== null && v !== '') {
            acc[k] = String(v);
          }
          return acc;
        },
        {} as Record<string, string>
      )
    ).toString();

    const response = await fetch(
      `${process.env.NEXT_PUBLIC_API_URL}/reports/guests/pdf?${query}`,
      { method: 'GET', credentials: 'include' }
    );

    if (!response.ok) {
      throw new Error('Failed to export guest logs PDF');
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `guests-${Date.now()}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },

};

// ── Supervisor Management ─────────────────────────────────────────

export const supervisorUsersApi = {
  list: (params?: {
    company_id?: string;
    skip?: number;
    limit?: number;
  }) =>
    apiClient.get(
      '/supervisor-user',
      params as QueryParams
    ),

  create: (payload: {
    email: string;
    password: string;
    full_name: string;
    phone: string;
    national_id: string;
    badge_number?: string;
    company_id: string;
    site_ids?: string[];
    shift_template_id?: string;
  }) =>
    apiClient.post(
      '/supervisor-user',
      payload
    ),

  update: (
    userId: string,
    payload: {
      full_name?: string;
      phone?: string;
      national_id?: string;
      badge_number?: string;
      site_ids?: string[];
      shift_template_id?: string;
    }
  ) =>
    apiClient.patch(
      `/supervisor-user/${userId}`,
      payload
    ),

  deactivate: (userId: string) =>
    apiClient.patch(
      `/supervisor-user/${userId}/deactivate`
    ),

  reactivate: (userId: string) =>
    apiClient.patch(
      `/supervisor-user/${userId}/reactivate`
    ),

  delete: (userId: string) =>
    apiClient.delete(
      `/supervisor-user/${userId}`
    ),
};

// ── Guard Management ──────────────────────────────────────────────

export const guardUsersApi = {
  list: (params?: {
    company_id?: string;
    page?: number;
    page_size?: number;
  }) =>
    apiClient.get(
      '/guard-user',
      params as QueryParams
    ),

  listOnDuty: (params?: {
    company_id?: string;
    page?: number;
    page_size?: number;
  }) =>
    apiClient.get(
      '/guard-user/on-duty',
      params as QueryParams
    ),

  create: (payload: any) =>
    apiClient.post(
      '/guard-user',
      payload
    ),

  update: (
    guardId: string,
    payload: any
  ) =>
    apiClient.patch(
      `/guard-user/${guardId}`,
      payload
    ),

  deactivate: (guardId: string) =>
    apiClient.patch(
      `/guard-user/${guardId}/deactivate`
    ),

  reactivate: (guardId: string) =>
    apiClient.patch(
      `/guard-user/${guardId}/reactivate`
    ),

  uploadPhoto: async (
    guardId: string,
    file: File
  ) => {
    const formData = new FormData();
    formData.append('file', file);

    return apiClient.post(
      `/guard-user/${guardId}/photo`,
      formData
    );
  },
};
export const leaveApi = {
  list: (params?: {
    company_id?: string;
    filter_status?: string;
    applicant_role?: string;
    search?: string;      // ← new
    page?: number;
    page_size?: number;
  }) => apiClient.get<LeaveListResponse>('/leave', params as QueryParams),
  decide: (leaveId: string, payload: LeaveDecisionPayload) =>
    apiClient.post(`/leave/${leaveId}/decide`, payload),
};

export const advancesApi = {
  list: (params?: {
    company_id?: string;
    filter_status?: string;
    applicant_role?: string;
    search?: string;      // ← new
    page?: number;
    page_size?: number;
  }) => apiClient.get<AdvanceListResponse>('/advances', params as QueryParams),
  decide: (advanceId: string, payload: AdvanceDecisionPayload) =>
    apiClient.post(`/advances/${advanceId}/decide`, payload),
  disburse: (advanceId: string) =>
    apiClient.post(`/advances/${advanceId}/disburse`, {}),
};

// ── Customer Dashboard (customer role — read-only, minimal) ───────────────
// Backend: app/api/v1/endpoints/customer_dashboard.py. Every route is
// scoped server-side to this customer's own sites (CustomerScope) — no
// company_id or site_id filtering is ever passed from the client for
// /overview, /sites, /staff. Deliberately minimal: who's assigned and
// who's on shift, nothing about SOS/incidents/shift editing.

export const customerDashboardApi = {
  getOverview: () =>
    apiClient.get<CustomerOverview>('/customer/overview'),

  getSites: () =>
    apiClient.get<CustomerSiteRow[]>('/customer/sites'),

  getStaff: () =>
    apiClient.get<CustomerStaffList>('/customer/staff'),

  getShifts: (params?: {
    date_from?: string;
    date_to?: string;
    status?: string;
    role?: 'guard' | 'supervisor';
    site_id?: string;
    skip?: number;
    limit?: number;
  }) => apiClient.get<CustomerShiftList>('/customer/shifts', params as QueryParams),
};

// ── Customer Management (soc / soc_admin / client — create customers & logins) ──
// Backend: app/api/v1/endpoints/customer_mgmt.py. Every call here except
// createCustomer needs company_id as a QUERY param, not body — the backend
// resolves it via resolve_company_id (client's own token supplies it
// automatically; soc/soc_admin must pass it explicitly since they can
// manage any company). apiClient.post/patch/delete don't support a params
// option, so the query string is built manually below, same pattern as
// clientDashboardApi.getGuards uses for GET.

function withCompanyId(path: string, companyId?: string): string {
  if (!companyId) return path;
  const sep = path.includes('?') ? '&' : '?';
  return `${path}${sep}company_id=${encodeURIComponent(companyId)}`;
}

export const customerMgmtApi = {
  listCustomers: (params?: { company_id?: string; is_active?: boolean }) =>
    apiClient.get<CustomerAdminRow[]>('/customer-mgmt/customers', params as QueryParams),

  createCustomer: (payload: CustomerAdminCreate) =>
    apiClient.post<CustomerAdminRow>('/customer-mgmt/customers', payload),

  getCustomer: (id: string, companyId?: string) =>
    apiClient.get<CustomerAdminRow>(withCompanyId(`/customer-mgmt/customers/${id}`, companyId)),

  updateCustomer: (id: string, payload: CustomerAdminUpdate, companyId?: string) =>
    apiClient.patch<CustomerAdminRow>(withCompanyId(`/customer-mgmt/customers/${id}`, companyId), payload),

  attachSites: (id: string, siteIds: string[], companyId?: string) =>
    apiClient.post<void>(withCompanyId(`/customer-mgmt/customers/${id}/sites`, companyId), { site_ids: siteIds }),

  detachSite: (siteId: string, companyId?: string) =>
    apiClient.delete<void>(withCompanyId(`/customer-mgmt/sites/${siteId}/customer`, companyId)),

  listCustomerUsers: (params?: { customer_id?: string; is_active?: boolean; company_id?: string }) =>
    apiClient.get<CustomerUserAdminListResponse>('/customer-mgmt/customer-users', params as QueryParams),

  createCustomerUser: (payload: CustomerUserAdminCreate, companyId?: string) =>
    apiClient.post<CustomerUserAdminRow>(withCompanyId('/customer-mgmt/customer-users', companyId), payload),

  updateCustomerUser: (id: string, payload: { full_name?: string; phone?: string; is_active?: boolean }, companyId?: string) =>
    apiClient.patch<CustomerUserAdminRow>(withCompanyId(`/customer-mgmt/customer-users/${id}`, companyId), payload),

  deactivateCustomerUser: (id: string, companyId?: string) =>
    apiClient.patch<CustomerUserAdminRow>(withCompanyId(`/customer-mgmt/customer-users/${id}/deactivate`, companyId)),

  reactivateCustomerUser: (id: string, companyId?: string) =>
    apiClient.patch<CustomerUserAdminRow>(withCompanyId(`/customer-mgmt/customer-users/${id}/reactivate`, companyId)),

  resetCustomerUserPassword: (id: string, companyId?: string) =>
    apiClient.post<{ message: string; temp_password: string }>(
      withCompanyId(`/customer-mgmt/customer-users/${id}/reset-password`, companyId)
    ),

  deleteCustomer: (id: string, companyId?: string) =>
    apiClient.delete<void>(withCompanyId(`/customer-mgmt/customers/${id}`, companyId)),

  deleteCustomerUser: (id: string, companyId?: string) =>
    apiClient.delete<void>(withCompanyId(`/customer-mgmt/customer-users/${id}`, companyId)),  
};